// PlatformUsers — 跨租戶使用者列表（Route 15, PR #106）。

import React, { useCallback, useEffect, useState } from "react";
import { Search, Users as UsersIcon, RefreshCw, ServerCrash, Inbox, ShieldCheck } from "lucide-react";
import EmptyState from "../../components/EmptyState.js";
import Skeleton from "../../components/Skeleton.js";
import { listPlatformUsers, friendlyPlatformError, type PlatformUser } from "../../lib/platformApi.js";
import { formatTaipeiDateTime } from "../../../lib/time.js";

type ListState =
  | { status: "loading" }
  | { status: "ready"; rows: PlatformUser[] }
  | { status: "error"; message: string };

export default function PlatformUsers(): React.ReactElement {
  const [state, setState] = useState<ListState>({ status: "loading" });
  const [q, setQ] = useState("");
  const [pendingQ, setPendingQ] = useState("");

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try { const r = await listPlatformUsers(pendingQ || null); setState({ status: "ready", rows: r.items }); }
    catch (e) { setState({ status: "error", message: friendlyPlatformError(e) }); }
  }, [pendingQ]);
  useEffect(() => { void load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-border bg-bg-surface p-4">
        <h2 className="text-sm font-semibold flex items-center gap-2 text-text-primary">
          <UsersIcon className="h-4 w-4 text-accent" /> 全部使用者
        </h2>
        <p className="text-2xs text-text-muted mt-1">所有已建立帳號的人員，可看到他們在不同租戶下的角色。</p>
      </div>
      <form onSubmit={(e) => { e.preventDefault(); setPendingQ(q); }} className="flex items-center gap-2">
        <label className="relative block flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-text-muted" />
          <input className="input pl-9" placeholder="搜尋 email 或顯示名稱（按 Enter）" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        <button type="button" className="btn-secondary text-sm" onClick={() => void load()} disabled={state.status === "loading"}>
          <RefreshCw className={`h-4 w-4 ${state.status === "loading" ? "animate-spin" : ""}`} /> 重新整理
        </button>
      </form>
      {state.status === "loading" && <div className="space-y-2">{[0,1,2,3,4].map(i => <div key={i} className="card p-4"><Skeleton className="h-9 w-full" /></div>)}</div>}
      {state.status === "error" && <EmptyState icon={ServerCrash} title="使用者列表載入失敗" description={state.message}
        action={<button className="btn-primary text-sm" onClick={() => void load()}>再試一次</button>} />}
      {state.status === "ready" && state.rows.length === 0 && <EmptyState icon={Inbox} title="目前沒有符合條件的使用者" />}
      {state.status === "ready" && state.rows.length > 0 && (
        <ul className="divide-y divide-border card overflow-hidden">
          {state.rows.map((u) => (
            <li key={u.id} className="p-4 flex items-start gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-subtle text-accent text-2xs font-semibold">{(u.display_name ?? u.email).slice(0,1).toUpperCase()}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-text-primary">{u.display_name ?? u.email.split("@")[0]}</span>
                  {u.super_admin && <span className="badge-warning inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" />平台管理員</span>}
                </div>
                <div className="text-2xs font-mono text-text-muted">{u.email}</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {u.memberships.map((m, i) => (
                    <span key={i} className="badge-default text-2xs">{m.tenant_id} · {m.role}</span>
                  ))}
                  {u.memberships.length === 0 && !u.super_admin && <span className="text-2xs text-text-muted">沒有租戶角色</span>}
                </div>
              </div>
              <div className="text-2xs font-mono text-text-muted">{u.last_login_at ? formatTaipeiDateTime(u.last_login_at) : "未登入"}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
